import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { environment } from 'environments/environment';
import { Observable, map } from 'rxjs';
import { MyWorkHistoryItem } from '../types/work/work.types';
import {
  ExpenseItem,
  ExpenseToCreate,
  ProjectOptions,
  ExpenseDetail,
  ExpenseProjects,
  CalculatedAmount
} from '@app/expenses/model/expense.types';
import { formatDate } from '@app/shared/utils/time.functions';

@Injectable({
  providedIn: 'root',
})
export class WorkService {
  private readonly http = inject(HttpClient);
  private readonly url = `${environment.url.api}/work/me`;

  getMyWorkHistory(): Observable<MyWorkHistoryItem[]> {
    return this.http.get<ApiMyWorkHistory>(`${this.url}/history`).pipe(map((response) => response.weeks));
  }

  getNextPaymentDate(): Observable<string> {
    return this.http
      .get<ApiNextPaymentDate>(`${this.url}/payment/date/next`)
      .pipe(map((response) => response.date?.toString()));
  }

  getExpenseList(year: number, month: number): Observable<ExpenseItem[]> {
    return this.http
      .get<ApiExpenses>(`${this.url}/expense?Year=${year}&Month=${month}`)
      .pipe(map((response) => response.expenses.map((expense) => ({ ...expense, date: expense.date.toString() }))));
  }

  getExpenseDetail(expenseId: string): Observable<ExpenseDetail> {
    return this.http.get<ExpenseDetail>(`${this.url}/expense/${expenseId}`);
  }

  getExpenseProjects(date: Date): Observable<ProjectOptions[]> {
    return this.http
      .get(`${this.url}/expense/config?Date=${formatDate(date, 'yyyy-MM-dd')}`)
      .pipe(map((response: ExpenseProjects) => response.projects));
  }

  calculateExpense(projectId: string, expenseType: string, date: Date, quantity: number): Observable<number> {
    return this.http
      .post(`${this.url}/expense/calculate`, {
        date: formatDate(date, 'yyyy-MM-dd'),
        projectId,
        expenseType,
        quantity,
      })
      .pipe(map((response: CalculatedAmount) => response.calculatedAmount));
  }

  createExpense(expenseToCreate: ExpenseToCreate): Observable<void> {
    const data = new FormData();

    data.append('Date', formatDate(expenseToCreate.date, 'yyyy-MM-dd'));
    data.append('ProjectId', expenseToCreate.projectId ?? '');
    data.append('CostCenterId', expenseToCreate.costCenterId ?? '');
    data.append('ExpenseType', expenseToCreate.expenseType ?? '');
    data.append('Quantity', expenseToCreate.quantity.toString() ?? '');
    data.append('Description', expenseToCreate.description ?? '');
    data.append('Document', expenseToCreate.document ?? '');

    return this.http.post<void>(`${this.url}/expense`, data);
  }

  deleteExpense(expenseId: string): Observable<any> {
    return this.http.delete(`${this.url}/expense/${expenseId}`).pipe(map((response: any) => response));
  }

  submitTransportation(enabled: boolean): Observable<void> {
    return this.http.post<void>(`${this.url}/transportation/submit`, { isEnabled: enabled });
  }
}

interface ApiNextPaymentDate {
  date: Date;
}

interface ApiMyWorkHistory {
  weeks: {
    year: number;
    weekNumber: number;
    workedHours: number;
    isApproved: boolean;
  }[];
}

interface ApiExpenses {
  expenses: {
    id: string;
    date: Date;
    companyName: string;
    projectName: string;
    expenseType: string;
    expenseTypeDescription: string;
    amount: number;
    state: number;
  }[];
}
